import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { startNotificationPoller, stopNotificationPoller } from '../redux/messages/actions';

/**
 * Hook para gestionar el polling automático de notificaciones
 *
 * Características:
 * - Inicia el poller cuando el componente se monta
 * - Detiene el poller cuando el componente se desmonta
 * - Solo se ejecuta si el usuario está autenticado
 * - Previene múltiples inicios si el hook se usa en varios componentes
 *
 * @param {Object} options - Opciones de configuración
 * @param {number} options.interval - Intervalo en milisegundos (default: 2 minutos)
 * @param {boolean} options.enabled - Si está habilitado (default: true)
 * @returns {Object} - Estado del poller
 *
 * @example
 * // En DashboardLayout.js
 * const { isPolling } = useNotificationPoller({ interval: 2 * 60 * 1000 });
 */
export const useNotificationPoller = (options = {}) => {
  const {
    interval = 2 * 60 * 1000, // 2 minutos por defecto
    enabled = true
  } = options;

  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state.auth?.isAuthenticated || false);
  const hasStarted = useRef(false);

  useEffect(() => {
    // Solo iniciar si:
    // 1. Está habilitado
    // 2. Usuario autenticado
    // 3. No se ha iniciado previamente (evitar múltiples inicios)
    if (enabled && isAuthenticated && !hasStarted.current) {
      dispatch(startNotificationPoller(interval));
      hasStarted.current = true;
    }

    // Si el usuario cierra sesión, detener el poller
    if (!isAuthenticated && hasStarted.current) {
      dispatch(stopNotificationPoller());
      hasStarted.current = false;
    }

    // Cleanup al desmontar el componente
    return () => {
      // Solo detener si este hook fue el que inició el poller
      // Y si el usuario ya no está autenticado
      if (hasStarted.current && !isAuthenticated) {
        dispatch(stopNotificationPoller());
        hasStarted.current = false;
      }
    };
  }, [dispatch, enabled, isAuthenticated, interval]);

  return {
    isPolling: hasStarted.current && isAuthenticated
  };
};

export default useNotificationPoller;
